// src/components/TabsHeader.jsx
import React from "react";
import {
  Alert,
  Box,
  Fade,
  MenuItem,
  Popper,
  Select,
  Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import TabNavigationBar from "./TabNavigationBar";
import { createToolbarSelectSx } from "./toolbarControlStyles";
import { TAB_VIEW_SIDE_PADDING_X } from "./tabViewContentSpacing";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const TEXT = "#000000";
const MUTED = "#A2ABB4";
const BORDER = "#D2D8DE";
const actionSelectSx = createToolbarSelectSx({
  height: 34,
  borderRadius: 4,
  backgroundColor: "#FFFFFF",
  textColor: TEXT,
  textFontSize: 14,
  textFontWeight: 400,
  outlineSx: {
    borderTop: `1px solid ${BORDER}`,
    borderRight: `1px solid ${BORDER}`,
    borderLeft: `1px solid ${BORDER}`,
    borderBottom: "none",
    borderRadius: "4px 4px 0 0",
  },
});

export default function ProfileHeader({
  title = "Company Profile",
  tab,
  onTabChange,
  tabs = [],
  counts = {},
  summaryRow = null,
  metaLabel,
  metaValue,
  onMetaValueClick,
  dateText,
  showAction = true,
  actionLabel = "",
  actionValue,
  onActionChange,
  actionPlaceholder = "New",
  actionPlaceholderValue = "",
  actionOptions = [],
  actionDisabled = false,
  actionSx = {},
  actionShowPlaceholderOption = true,
  actionFeedback = null,
  onActionFeedbackClose,
  sx = {},
}) {
  const safeTabs = Array.isArray(tabs) ? tabs : [];
  const safeActionOptions = Array.isArray(actionOptions) ? actionOptions : [];
  const includePlaceholderActionOption =
    actionShowPlaceholderOption &&
    !safeActionOptions.some(
      (option) =>
        String(option?.value ?? "") === String(actionPlaceholderValue ?? "") ||
        String(option?.label || "").trim() === String(actionPlaceholder || "").trim()
    );
  const actionAnchorRef = React.useRef(null);
  const actionFeedbackOpen = Boolean(actionFeedback?.open && actionFeedback?.message);

  React.useEffect(() => {
    if (!actionFeedbackOpen || typeof onActionFeedbackClose !== "function") return undefined;

    const timeout = window.setTimeout(() => {
      onActionFeedbackClose();
    }, Number(actionFeedback?.autoHideDuration || 2500));

    return () => window.clearTimeout(timeout);
  }, [actionFeedback?.autoHideDuration, actionFeedbackOpen, onActionFeedbackClose]);

  return (
    <Box
      sx={{
        mx: 0,
        px: TAB_VIEW_SIDE_PADDING_X,
        pt: 0,
        background: "#fff",
        width: "100%",
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          columnGap: 2,
          rowGap: 1,
          alignItems: "flex-start",
        }}
      >
        <Typography
          className="profile-header-title"
          sx={{
            color: BLUE,
            fontWeight: 700,
            fontSize: 20,
            lineHeight: "30px",
            mb: 0.6,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            flexWrap: "wrap",
            gap: 1,
            minWidth: 0,
            minHeight: 24,
          }}
        >
          {metaLabel ? (
            <Typography
              className="profile-header-meta-label"
              sx={{ fontSize: 14, color: TEXT, fontWeight: 400 }}
            >
              {metaLabel}:
            </Typography>
          ) : null}

          {metaValue ? (
            <Typography
              className="profile-header-meta-value"
              component={onMetaValueClick ? "button" : "span"}
              onClick={onMetaValueClick}
              sx={{
                fontSize: 14,
                color: PURPLE,
                fontWeight: 400,
                ...(onMetaValueClick
                  ? {
                      border: 0,
                      p: 0,
                      background: "transparent",
                      cursor: "pointer",
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }
                  : null),
              }}
            >
              {metaValue}
            </Typography>
          ) : null}

          {dateText ? (
            <>
              <Typography
                className="profile-header-date-divider"
                sx={{ fontSize: 14, color: MUTED, fontWeight: 400 }}
              >
                |
              </Typography>
              <Typography
                className="profile-header-date-text"
                sx={{ fontSize: 14, color: MUTED, fontWeight: 400 }}
              >
                {dateText}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>

      <Box
        className="profile-header-tab-row"
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          columnGap: 2,
          rowGap: 1.5,
          alignItems: { xs: "stretch", md: "end" },
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "1px",
            backgroundColor: BORDER,
            zIndex: 2,
            pointerEvents: "none",
          },
        }}
      >
        <TabNavigationBar
          wrapperSx={{ minWidth: 0, maxWidth: "100%", position: "relative", zIndex: 3 }}
          tabsClassName="profile-header-tabs"
          value={tab}
          onChange={(value) => onTabChange?.(value)}
          items={safeTabs.map((item) => {
            const rawCount =
              item?.count !== undefined
                ? item.count
                : item?.countKey && counts && typeof counts === "object"
                  ? counts[item.countKey]
                  : undefined;
            const normalizedCount =
              rawCount === undefined || rawCount === null || rawCount === ""
                ? undefined
                : Number(rawCount);

            return {
              value: item.value,
              label: item.label,
              count: Number.isFinite(normalizedCount) ? normalizedCount : undefined,
            };
          })}
          borderBottom={false}
          contentSizedIndicator={false}
          contentSizedIndicatorHeight={2}
          indicatorHeight={2}
          activeColor={BLUE}
          idleColor={PURPLE}
          labelSx={{ color: "inherit" }}
          countSx={{ fontSize: 12, lineHeight: "18px", height: 22, px: 1, backgroundColor: "#FAFAFA", color: "#A2ABB4" }}
          tabGap={3}
          tabsSx={{
            pt: 2,
            minHeight: 48,
            "& .MuiTab-root": {
              padding: 0,
              color: PURPLE,
            },
          }}
        />

        {showAction ? (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: { xs: "stretch", md: "flex-end" },
              alignItems: { xs: "stretch", md: "center" },
              flexWrap: "wrap",
              gap: 1,
              minWidth: 0,
              width: { xs: "100%", md: "auto" },
              pb: 0,
            }}
          >
            {actionLabel ? (
              <Typography
                className="profile-header-action-label"
                sx={{
                  fontSize: 14,
                  color: TEXT,
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                  alignSelf: "center",
                }}
              >
                {actionLabel}:
              </Typography>
            ) : null}

            <Box ref={actionAnchorRef} sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0 }}>
              <Select
                className="profile-header-action-select"
                size="small"
                displayEmpty
                value={actionValue ?? ""}
                onChange={(event) => onActionChange?.(event.target.value)}
                disabled={actionDisabled}
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: 0, sm: 170 },
                  ...actionSelectSx,
                  ...actionSx,
                  "& .MuiSelect-select": {
                    ...actionSelectSx["& .MuiSelect-select"],
                    ...actionSx["& .MuiSelect-select"],
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      minWidth: actionAnchorRef.current?.offsetWidth || 170,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "4px",
                      boxShadow: "0 12px 24px rgba(15, 23, 42, 0.08)",
                      overflow: "hidden",
                      "& .MuiMenuItem-root": {
                        minHeight: 30,
                        px: 1.5,
                        fontSize: 14,
                        fontWeight: 400,
                        color: TEXT,
                        borderBottom: `1px solid ${BORDER}`,
                      },
                      "& .MuiMenuItem-root:last-of-type": {
                        borderBottom: "none",
                      },
                    },
                  },
                }}
                renderValue={(value) => {
                  const matchedOption = safeActionOptions.find(
                    (option) => String(option?.value ?? "") === String(value ?? "")
                  );

                  if (
                    !matchedOption &&
                    (value === undefined ||
                      value === null ||
                      String(value) === "" ||
                      String(value) === String(actionPlaceholderValue ?? ""))
                  ) {
                    return (
                      <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>
                        {actionPlaceholder}
                      </Box>
                    );
                  }

                  return matchedOption?.label || String(value);
                }}
              >
                {includePlaceholderActionOption ? (
                  <MenuItem
                    value={actionPlaceholderValue ?? ""}
                    sx={{ fontWeight: 400, fontStyle: "normal" }}
                  >
                    {actionPlaceholder}
                  </MenuItem>
                ) : null}

                {safeActionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontWeight: 400, ...option.sx }}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Popper
              open={Boolean(actionFeedbackOpen && actionAnchorRef.current)}
              anchorEl={actionAnchorRef.current}
              placement="top"
              transition
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 10],
                  },
                },
              ]}
              sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={180}>
                  <Alert
                    severity={actionFeedback?.severity || "success"}
                    onClose={() => onActionFeedbackClose?.()}
                    sx={{
                      width: "max-content",
                      maxWidth: 320,
                      boxShadow: "0 14px 32px rgba(15,23,42,0.16)",
                      "& .MuiAlert-message": {
                        fontSize: 12,
                        lineHeight: 1.35,
                      },
                    }}
                  >
                    {actionFeedback?.message || ""}
                  </Alert>
                </Fade>
              )}
            </Popper>
          </Box>
        ) : (
          <Box sx={{ minWidth: 0, width: { xs: "100%", md: 260 } }} />
        )}
      </Box>

      {summaryRow ? <Box sx={{ pt: 1.15 }}>{summaryRow}</Box> : null}
    </Box>
  );
}
